import { includes, keys, pickBy, some } from "lodash";
import { cacheRecall } from "../actions";
import { ThunkActionType } from "../actions/types";
import { StoreState } from "../../models/states/StoreState";
import { Piping } from "../../piping";
import { RecallType } from "../../piping/RecallType";
import { cacheKey, getRecall } from "../reducers/recallReducer";

export const evaluateRecalls = (
    expression: string,
    mode: RecallType,
    dependencies: string[] = [],
    evaluationStack: string[] = [],
): ThunkActionType<string> => {
    const selectRecall = (state: StoreState) => state.recall;
    const selectInterview = (state: StoreState) => state.interview;

    return (dispatch, getState) => {
        const state = selectRecall(getState());
        const key = cacheKey(expression, mode);
        // Recall is already in the cache.
        const recalledTextId = state.recalledTextIdByKey[key];
        if (recalledTextId !== undefined) {
            // Add variable dependencies for recall in the cache.
            const recalledTextVariables = keys(
                pickBy(state.recalledTextIdsByVariableName, (ids) => ids.includes(recalledTextId)),
            );

            recalledTextVariables
                .filter((v) => !dependencies.includes(v))
                .forEach((v) => dependencies.push(v));

            return getRecall(state, expression, mode);
        }
        // Add variable dependencies.
        const pipingVariables = Piping.identifyVariables(expression);
        pipingVariables
            .filter((v) => !dependencies.includes(v))
            .forEach((v) => dependencies.push(v));
        let result = expression;

        // Evaluate single recall until it finds matched string(without brackets)
        while (/^\[([^\]]+)\]$/.test(result)) {
            if (evaluationStack.includes(result)) {
                dispatch(cacheRecall(expression, mode, result, dependencies));
                return result;
            }
            evaluationStack.push(result);

            // Evaluate the recall.
            result = Piping.evaluateRecalls(selectInterview(getState()), result, mode);

            // Return an error if the result is the same as the input.
            if (result === expression) {
                dispatch(cacheRecall(expression, mode, result, dependencies));
                return result;
            }
        }

        // Evaluate the expression until every single recall are evaluated
        const pipingRegex = /\[[^[\]]+\]/g;
        while (some(result.match(pipingRegex), (m) => !includes(evaluationStack, m))) {
            // Replace each remaining recall recursively.
            result = result.replace(pipingRegex, (recall) => {
                const recallResult = dispatch(
                    evaluateRecalls(recall, mode, dependencies, evaluationStack),
                );
                if (!includes(evaluationStack, recall)) evaluationStack.push(recall);
                return recallResult;
            });
        }
        dispatch(cacheRecall(expression, mode, result, dependencies));
        return result;
    };
};
